import React from 'react';
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from 'recharts';
import { Storefront, BuildCircle, PendingActions, Payments } from '@mui/icons-material';
import StatCard from './StatCard';
import ChartCard from './ChartCard';
import TableCard from './TableCard';
import ChartTooltip from './ChartTooltip';
import StatusChip from '../shared/StatusChip';
import { COLORS } from '../shared/tokens';
import { gbp, gbpCompact, shortWeek, SERIES, CATEGORY_PALETTE } from './format';

export default function AdminDashboard({ data }) {
    const { kpis, trend = [], topShops = [], recent = [] } = data;
    const spark = trend.map((t) => ({ v: t.cash + t.card + t.other }));

    return (
        <>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 16 }}>
                <StatCard label="8-Week Sales" value={gbpCompact(kpis.sales8)} sub={`through ${kpis.latestWeekCode}`} accent={COLORS.primary} icon={<Payments fontSize="small" />} spark={spark} />
                <StatCard label="Latest Week" value={gbpCompact(kpis.latestWeek)} sub={kpis.latestWeekCode} accent="#2563eb" icon={<Payments fontSize="small" />} />
                <StatCard label="Active Shops" value={kpis.activeShops} accent="#15803d" icon={<Storefront fontSize="small" />} />
                <StatCard label="Jobs In Process" value={kpis.inProcess} accent="#a16207" icon={<BuildCircle fontSize="small" />} />
                <StatCard label="Pending Approvals" value={kpis.pendingApprovals} accent="#b91c1c" icon={<PendingActions fontSize="small" />} />
            </div>

            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 16 }}>
                <ChartCard
                    title="Weekly Sales" flex="2 1 460px"
                    legend={[{ label: 'Cash', color: SERIES.cash }, { label: 'Card', color: SERIES.card }, { label: 'Other', color: SERIES.other }]}
                >
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={trend} margin={{ top: 6, right: 8, left: -12, bottom: 0 }} barSize={20}>
                            <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} vertical={false} />
                            <XAxis dataKey="week" tickFormatter={shortWeek} tick={{ fill: COLORS.textDim, fontSize: 11 }} axisLine={false} tickLine={false} />
                            <YAxis tickFormatter={(v) => gbpCompact(v)} tick={{ fill: COLORS.textDim, fontSize: 11 }} axisLine={false} tickLine={false} width={54} />
                            <Tooltip cursor={{ fill: COLORS.primaryTintSoft }} content={<ChartTooltip week />} />
                            <Bar dataKey="cash" stackId="s" fill={SERIES.cash} />
                            <Bar dataKey="card" stackId="s" fill={SERIES.card} />
                            <Bar dataKey="other" stackId="s" fill={SERIES.other} radius={[3, 3, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="Top Shops" subtitle="by sales this period" flex="1 1 320px">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart layout="vertical" data={topShops} margin={{ top: 0, right: 12, left: 8, bottom: 0 }} barSize={14}>
                            <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} horizontal={false} />
                            <XAxis type="number" tickFormatter={(v) => gbpCompact(v)} tick={{ fill: COLORS.textDim, fontSize: 10 }} axisLine={false} tickLine={false} />
                            <YAxis type="category" dataKey="name" width={100} tick={{ fill: COLORS.text, fontSize: 11 }} axisLine={false} tickLine={false} />
                            <Tooltip cursor={{ fill: COLORS.primaryTintSoft }} content={<ChartTooltip />} />
                            <Bar dataKey="total" radius={[0, 4, 4, 0]}>
                                {topShops.map((_, i) => <Cell key={i} fill={CATEGORY_PALETTE[i % CATEGORY_PALETTE.length]} />)}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>

            <TableCard
                title="Recent Weekly Sales"
                columns={[
                    { key: 'week', label: 'Week', render: (r) => shortWeek(r.week) },
                    { key: 'shop', label: 'Shop' },
                    { key: 'cash', label: 'Cash', align: 'right', render: (r) => gbp(r.cash) },
                    { key: 'card', label: 'Card', align: 'right', render: (r) => gbp(r.card) },
                    { key: 'other', label: 'Other', align: 'right', render: (r) => gbp(r.other) },
                    { key: 'status', label: 'Status', render: (r) => <StatusChip label={r.status} /> },
                ]}
                rows={recent}
                emptyLabel="No weekly sales recorded yet"
            />
        </>
    );
}
